import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EndpointService } from '../endpoints.service';
import {
  TrainingPackageResponse,
  TrainingSessionResponse,
  TrainingRegistrationResponse,
  TrainingSessionAssignmentResponse,
  TrainingDayCapacityResponse
} from '../models/response.module';
import {
  AQH_TRN_0, AQH_TRN_1, AQH_TRN_2, AQH_TRN_3,
  AQH_TRN_10, AQH_TRN_11, AQH_TRN_12, AQH_TRN_13,
  AQH_TRN_20, AQH_TRN_21, AQH_TRN_22, AQH_TRN_23,
  AQH_TRN_30, AQH_TRN_31, AQH_TRN_32, AQH_TRN_33, AQH_TRN_34,
  AQH_TRN_40, AQH_TRN_41, AQH_TRN_42, AQH_TRN_43, AQH_TRN_44, AQH_TRN_45
} from '../models/input.module';

@Injectable({
  providedIn: 'root'
})
export class TrainingService {

  constructor(
    private endpointService: EndpointService
  ) {}

  // ============================================================================
  // PACKAGES
  // ============================================================================

  getPackages(params: AQH_TRN_0): Observable<TrainingPackageResponse[]> {
    return this.endpointService.aqh_trn_0(params)
      .pipe(map(res => res.data || []));
  }

  createPackage(data: AQH_TRN_1): Observable<TrainingPackageResponse> {
    return this.endpointService.aqh_trn_1(data)
      .pipe(map(res => res.data));
  }

  updatePackage(data: AQH_TRN_2): Observable<TrainingPackageResponse> {
    return this.endpointService.aqh_trn_2(data)
      .pipe(map(res => res.data));
  }

  deletePackage(package_id: number): Observable<void> {
    return this.endpointService.aqh_trn_3({ package_id })
      .pipe(map(() => undefined));
  }

  // ============================================================================
  // SESSIONS
  // ============================================================================

  getSessions(params: AQH_TRN_10): Observable<TrainingSessionResponse[]> {
    return this.endpointService.aqh_trn_10(params)
      .pipe(map(res => res.data || []));
  }

  getPublicSessions(params: AQH_TRN_10): Observable<TrainingSessionResponse[]> {
    return this.endpointService.aqh_trn_10_public(params)
      .pipe(map(res => res.data || []));
  }

  createSession(data: AQH_TRN_11): Observable<TrainingSessionResponse> {
    return this.endpointService.aqh_trn_11(data)
      .pipe(map(res => res.data));
  }

  updateSession(data: AQH_TRN_12): Observable<TrainingSessionResponse> {
    return this.endpointService.aqh_trn_12(data)
      .pipe(map(res => res.data));
  }

  deleteSession(session_id: number): Observable<void> {
    return this.endpointService.aqh_trn_13({ session_id })
      .pipe(map(() => undefined));
  }

  // ============================================================================
  // REGISTRATIONS
  // ============================================================================

  getRegistrations(params: AQH_TRN_20): Observable<TrainingRegistrationResponse[]> {
    return this.endpointService.aqh_trn_20(params)
      .pipe(map(res => res.data || []));
  }

  createRegistration(data: AQH_TRN_21): Observable<TrainingRegistrationResponse> {
    return this.endpointService.aqh_trn_21(data)
      .pipe(map(res => res.data));
  }

  updateRegistration(data: AQH_TRN_22): Observable<TrainingRegistrationResponse> {
    return this.endpointService.aqh_trn_22(data)
      .pipe(map(res => res.data));
  }

  deleteRegistration(registration_id: number): Observable<void> {
    return this.endpointService.aqh_trn_23({ registration_id })
      .pipe(map(() => undefined));
  }

  // ============================================================================
  // ASSIGNMENTS
  // ============================================================================

  getAssignments(params: AQH_TRN_30): Observable<TrainingSessionAssignmentResponse[]> {
    return this.endpointService.aqh_trn_30(params)
      .pipe(map(res => res.data || []));
  }

  createAssignment(data: AQH_TRN_31): Observable<TrainingSessionAssignmentResponse> {
    return this.endpointService.aqh_trn_31(data)
      .pipe(map(res => res.data));
  }

  updateAssignment(data: AQH_TRN_32): Observable<TrainingSessionAssignmentResponse> {
    return this.endpointService.aqh_trn_32(data)
      .pipe(map(res => res.data));
  }

  deleteAssignment(assignment_id: number): Observable<void> {
    return this.endpointService.aqh_trn_33({ assignment_id })
      .pipe(map(() => undefined));
  }

  bulkAssign(data: AQH_TRN_34): Observable<{ created: TrainingSessionAssignmentResponse[]; errors: any[] }> {
    return this.endpointService.aqh_trn_34(data)
      .pipe(map(res => res.data));
  }

  // ============================================================================
  // DAY CAPACITY
  // ============================================================================

  getDayCapacity(params: AQH_TRN_40): Observable<TrainingDayCapacityResponse[]> {
    return this.endpointService.aqh_trn_40(params)
      .pipe(map(res => res.data || []));
  }

  setDayCapacity(data: AQH_TRN_41): Observable<TrainingDayCapacityResponse> {
    return this.endpointService.aqh_trn_41(data)
      .pipe(map(res => res.data));
  }

  deleteDayCapacity(capacity_id: number): Observable<void> {
    return this.endpointService.aqh_trn_42({ capacity_id })
      .pipe(map(() => undefined));
  }

  // ============================================================================
  // PACKAGE DAYS
  // ============================================================================

  setPackageDays(data: AQH_TRN_43): Observable<TrainingPackageResponse> {
    return this.endpointService.aqh_trn_43(data)
      .pipe(map(res => res.data));
  }

  // ============================================================================
  // PACKAGES WITH AVAILABILITY
  // ============================================================================

  getPackagesWithAvailability(params: AQH_TRN_44): Observable<TrainingPackageResponse[]> {
    return this.endpointService.aqh_trn_44(params)
      .pipe(map(res => res.data || []));
  }

  // ============================================================================
  // PUBLIC SESSION ASSIGNMENTS
  // ============================================================================

  getPublicSessionAssignments(params: AQH_TRN_45): Observable<TrainingSessionAssignmentResponse[]> {
    return this.endpointService.aqh_trn_45(params)
      .pipe(map(res => res.data || []));
  }
}
